"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar as CalendarIcon, CircleCheck, ShieldCheck, ArrowRight, Loader, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const textareaClasses = "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
function DemoForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="animate-in fade-in zoom-in duration-500 flex flex-col items-center justify-center space-y-4 py-12 text-center">
        <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/20 text-primary">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Request received</h3>
        <p className="max-w-md text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>
                Full Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input type="text" placeholder="John Doe" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>
                Email Address <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input type="email" placeholder="john@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>
                Company / Organization <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input type="text" placeholder="Acme Agency" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="job_title" render={({
        field
      }) => <FormItem>
              <FormLabel>Job Title / Role</FormLabel>
              <FormControl>
                <input type="text" placeholder="Founder / Agency Lead" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>
                Phone Number <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <input type="tel" placeholder="+1 555-123-4567" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_demo_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Demo Date</FormLabel>
              <FormControl>
                <input type="date" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription>Optional, if you already have a preferred date.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="use_case_description" render={({
      field
    }) => <FormItem>
            <FormLabel>Use Case / Delivery Model</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us briefly how you want to use EloRAG white-label for client delivery." className={textareaClasses} {...field} />
            </FormControl>
            <FormDescription>
              This helps tailor the conversation to your agency or reseller model.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent_contact" rules={{
      required: "You must agree to be contacted to proceed."
    }} render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border bg-muted/20 p-4">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-sm border-primary text-primary accent-primary focus:ring-primary" checked={value} onChange={e => onChange(e.target.checked)} {...fieldProps} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-medium leading-none">
                I agree to be contacted regarding this demo request. <span className="text-destructive">*</span>
              </FormLabel>
              <FormDescription className="text-xs">
                We will only use this information to arrange your demo.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="flex justify-end pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="min-w-[220px] h-12 w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Submitting...
            </> : <>
              Request Demo
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function RequestAWhiteLabelDemo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="request-a-white-label-demo" className="relative overflow-hidden bg-background py-16 lg:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[920px] -translate-x-1/2 opacity-20">
        <div className="absolute inset-0 rounded-full bg-gradient-to-b from-primary/30 to-transparent blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={cn("mx-auto max-w-5xl transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <Card className="rounded-3xl border border-border/70 bg-card p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.16)] md:p-10 lg:p-12">
            <div className="grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  <Layers3 className="size-4" />
                  <span>White-label demo</span>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-[2.7rem] leading-[1.05]">
                  See how EloRAG fits your agency delivery model
                </h2>

                <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                  Book a live walkthrough to see how branded RAG delivery, approved-only retrieval,
                  citation-based answers, and multi-tenant governance can fit your agency or reseller model.
                </p>

                <div className="mt-7 space-y-4">
                  {["White-label delivery model walkthrough", "Governance and tenant isolation overview", "Capacity and pricing fit discussion", "Agency use case review"].map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-1 rounded-full bg-primary/15 p-1">
                        <CircleCheck className="size-4 text-primary" />
                      </div>
                      <span className="text-foreground/90">{feature}</span>
                    </div>)}
                </div>

                <div className="mt-7 rounded-xl border border-border/70 bg-secondary/30 p-4 text-sm leading-relaxed text-muted-foreground">
                  Best suited for agencies, resellers, and operators evaluating branded AI delivery for multiple client environments.
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-2xl border border-border/70 bg-background p-6 text-center shadow-[0_12px_30px_-18px_rgba(0,0,0,0.12)]">
                  <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10">
                    <SafeHydrate><CalendarIcon className="size-8 text-primary" /></SafeHydrate>
                  </div>

                  <h3 className="text-2xl font-semibold text-foreground">Book your live demo</h3>
                  <p className="mt-3 text-muted-foreground">
                    Start with a guided conversation about fit, white-label structure, and next steps.
                  </p>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="lg" className="mt-8 h-12 w-full text-base shadow-sm">
                        Book a Demo Now
                      </Button>
                    </DialogTrigger>

                    <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-border bg-card p-0 sm:rounded-2xl">
                      <div className="border-b border-border bg-muted/30 p-6 md:p-8">
                        <DialogHeader>
                          <DialogTitle className="text-2xl">Request a White-Label Demo</DialogTitle>
                          <DialogDescription className="mt-2 text-base">
                            Fill out the form below and our team will contact you to arrange a tailored walkthrough.
                          </DialogDescription>
                        </DialogHeader>
                      </div>

                      <div className="p-6 md:p-8">
                        <FormProvider formId="request_demo_form_white_label_for_agencies" sectionName="White-Label Demo Request" defaultValues={{
                        full_name: "",
                        email: "",
                        company_name: "",
                        job_title: "",
                        phone_number: "",
                        preferred_demo_date: "",
                        use_case_description: "",
                        consent_contact: false
                      }} submitText="Request Demo" successMessage="Thank you. Your demo request has been received and our team will contact you shortly.">
                          <DemoForm />
                        </FormProvider>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <p className="mt-5 text-xs text-muted-foreground">
                    No credit card required. No obligation.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}